/**
 * Transforms an array of station and/or group IDs into a structured array of objects
 * containing a label and corresponding station ID(s) which is required to fetch data
 * from the analytics backend.
 *
 * @param {Array<Object>} stations - An array of stations as returned by the useStations hook
 * @param {Array<Object>} groups - An array of groups as returned by the useStationGroups hook
 * @param {string[]} idsArray - An array of string ID(s). Each ID either represents a station
 *   (e.g. stat-123) or a group ID (e.g parent-group-1__child-group-a).
 * 
 * @returns {{ label: string, ids: string[] }[]} An array of objects, each with a 'label'
 *   (the station or group name) and an 'ids' array of corresponding station ID(s).
 *
 * @example
 * // Given a station with id "stat-1" and name "Station A"
 * // and a group with groupId "parent-group-1__child-group-a", name "Child Group A", stationIds ["stat-1", "stat-2"]
 * createStationsAndGroupsInput(["stat-1", "parent-group-1__child-group-a"])
 * // Returns:
 * // [
 * //   { label: "Station A", ids: ["stat-1"] },
 * //   { label: "Child Group A", ids: ["stat-1", "stat-2"] }
 * // ]
 */
const createStationsAndGroupsInput = (stations, groups, idsArray) => {
  return idsArray.map((value) => {
    if (value.startsWith("stat-")) {
      const station = stations.find((s) => s.id === value)
      return {
        label: station?.name,
        ids: [value],
      }
    } else {
      // Flatten all childGroups across all groupSets
      const allChildGroups = groups.flatMap((groupSet) => groupSet.childGroups)
      const matchedGroup = allChildGroups.find((group) => group.id === value)
      return {
        label: matchedGroup?.name,
        ids: matchedGroup?.stationGuids || [],
      }
    }
  })
}

/**
 * Creates a unique list of station IDs from a mix of station IDs and group IDs.
 *
 * - Station IDs (prefixed with `"stat-"`) are added directly.
 * - We get stations IDs related to a group ID, and all associated station IDs are added.
 * - A `Set` is used to guarantee uniqueness.
 *
 * @param {Array<Object>} groups
 *   Array of group sets containing child groups.
 *
 * @param {Array<string>} idsArray
 *   Array of IDs that may contain station IDs (prefixed with `"stat-"`) and/or group IDs.
 *
 * @returns {Array<string>}
 *   A deduplicated array of station IDs.
 */
const createStationsInputFromStationsAndGroups = (groups, idsArray) => {
  const stationIds = new Set() // Creating a Set ensures uniqueness

  // Flatten all childGroups once
  const allChildGroups = groups.flatMap(
    (groupSet) => groupSet.childGroups ?? []
  )

  idsArray.forEach((value) => {
    if (value.startsWith("stat-")) {
      stationIds.add(value)
    } else {
      const matchedGroup = allChildGroups.find(
        (group) => group.id === value
      )

      matchedGroup?.stationGuids?.forEach((id) => {
        stationIds.add(id)
      })
    }
  })

  return Array.from(stationIds)
}

export { createStationsAndGroupsInput, createStationsInputFromStationsAndGroups }
